import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router';

import { ProgressSpinner } from 'primereact/progressspinner';
import { Button, Textarea, TextField } from '@marketplace/ui-elements';
import { Icon } from '@developer/components';
import { useAPIFetchApplicationDescription } from '@marketplace/api';
import { joiResolver } from '@hookform/resolvers/joi';

import { Uploader } from './uploader-logo';
import { UploaderImg } from './uploader-app-img';
import { descriptionValidationSchema } from './description.validation';
import {
  TDescriptionProps,
  TDescriptionFormData,
  TDescriptionSentFormData,
  TApplicationsParams,
} from './description-types';

import classes from './descriptions-form.module.scss';

export const DescriptionForm = ({
  currentApplication,
  isPublished,
  apiResponseErrors,
  onChangeIsApplicationInEditMode,
  isReadyToPublish
}: TDescriptionProps) => {
  const { formatMessage } = useIntl();
  const [isContentEditable, setIsContentEditable] = useState(false);
  const [initialData, setInitialData] = useState<Omit<TDescriptionFormData, 'application_id'> | any>({
    url: '',
    long_description: '',
    icon: null,
    preview_images: [],
  });
  type TImagesUrl = {
    preview_images_url: string[];
    recovery_images_url: string[];
  };
  const [logo, setoLogo] = useState<any>(null);
  const [images, setImages] = useState([]);
  const [imagesUrl, setImagesUrl] = useState<TImagesUrl>({
    preview_images_url: [],
    recovery_images_url: [],
  });
  const [errorImg, setErrorImg] = useState('');

  const { id } = useParams<TApplicationsParams>();

  const { data, loaded, updateDescription, addDescription, loading } = useAPIFetchApplicationDescription({
    applicationId: currentApplication?.id || id,
  });

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    setError,
    getValues,
    clearErrors,
    trigger,
    formState: { isValid, errors, dirtyFields },
  } = useForm<TDescriptionFormData>({
    resolver: joiResolver(descriptionValidationSchema({ formatMessage })),
    mode: 'onChange',
  });

  const isDirty =
    !!Object.keys(dirtyFields)?.length ||
    imagesUrl.recovery_images_url?.length > 0 ||
    getValues('preview_images')?.length > 0;

  useEffect(() => {
    if (loaded) {
      const arrUrlImg: string[] = [];
      data.preview_images.forEach((element: any) => {
        arrUrlImg.push(element.url);
      });

      setInitialData({
        url: data?.url ?? '',
        long_description: data?.long_description ?? '',
        icon: data?.icon ?? '',
        preview_images: [],
      });

      setImagesUrl({
        preview_images_url: arrUrlImg,
        recovery_images_url: [],
      });
    }
  }, [data]);

  useEffect(() => {
    if (loaded) reset(initialData);
  }, [initialData]);

  const isNewDescription = !data?.url;
  const [errorList, setErrorList] = useState<any>([]);

  const setResponseErrors = (response: any) => {
    const errorsList = response?.errors?.reduce((acc: any, item: any) => {
      const errorfield = item.source;
      return { ...acc, [errorfield]: item.message };
    }, {});
    setErrorList(errorsList);
  };

  useEffect(() => {
    if (data?.errors) {
      setResponseErrors(data);
    }else{
      isEditOff()
    }
  }, [data]);

  const saveDescription = (body: TDescriptionFormData) => {
    const bodySent: TDescriptionSentFormData = {
      url: body.url,
      long_description: body.long_description,
      icon: body.icon,
      preview_images: [],
    };

    if (body.preview_images) {
      bodySent.preview_images = [...body.preview_images, ...imagesUrl?.preview_images_url];
    } else {
      bodySent.preview_images = [...imagesUrl.preview_images_url];
    }
    if (isPublished) {
      updateDescription(bodySent);
      setImages([]);
      setErrorImg('');
      
    } else {
      isNewDescription ? addDescription(bodySent) : updateDescription(bodySent);
    }
 
    if (!data?.errors && !isPublished) {
      setImages([]);
      setErrorImg('');
      setInitialData({ ...initialData, preview_images: [] });
      isEditOff();
    }
    if (isReadyToPublish) {
      updateDescription(bodySent);
      setImages([]);
      setErrorImg('');
    } else {
      isNewDescription ? addDescription(bodySent) : updateDescription(bodySent);
    }

    if (!data?.errors && !isReadyToPublish) {
      setImages([]);
      setErrorImg('');
      setInitialData({ ...initialData, preview_images: [] });
      isEditOff();
    }
  };

  useEffect(() => {
    if (apiResponseErrors?.url || errorList?.url) {
      setError('url', {
        message: formatMessage(
          { id: 'generalForm_validation_required' },
          {
            field: formatMessage({
              id: 'description_descriptionURL',
            }),
          }
        ),
      });
    }

    if (apiResponseErrors?.long_description || errorList?.long_description) {
      setError('long_description', {
        message: formatMessage(
          { id: 'generalForm_validation_required' },
          {
            field: formatMessage({
              id: 'description_descriptionLabel',
            }),
          }
        ),
      });
    }

    if (apiResponseErrors?.preview_images || errorList?.preview_images) {
      setError('preview_images', {
        message: formatMessage(
          { id: 'generalForm_validation_required' },
          {
            field: formatMessage({
              id: 'description_preview_img',
            }),
          }
        ),
      });
    }

    return () => {
      clearErrors();
    };
  }, [apiResponseErrors, errorList]);

  const isEditOn = () => setIsContentEditable(true);
  const isEditOff = () => setIsContentEditable(false);

  useEffect(() => {
    onChangeIsApplicationInEditMode(onChangeIsApplicationInEditMode?.DESCRIPTION_TAB, isContentEditable);
  }, [isContentEditable]);

  const handleCancelEdit = () => {
    setoLogo(null);
    setImages([]);
    setImagesUrl({
      recovery_images_url: [],
      preview_images_url: [...imagesUrl.recovery_images_url, ...imagesUrl.preview_images_url],
    });
    setErrorImg('');
    reset(initialData);
    isEditOff();
  };

  const onRemoveImgUrl = (index: number) => {
    const arrImg = imagesUrl.preview_images_url;
    const deteteUrl = arrImg.splice(index, 1);
    setImagesUrl({
      recovery_images_url: [...imagesUrl.recovery_images_url, ...deteteUrl],
      preview_images_url: [...imagesUrl.preview_images_url],
    });
  };

  return (
    <div className={classes.descriptionForm__wrapper}>
      <Icon
        classes={isContentEditable ? classes.editIcon_active : classes.editIcon}
        variant="edit-pencil"
        onClick={isEditOn}
        isPointer
      />
      <form onSubmit={handleSubmit(saveDescription)} className={classes.form}>
        <div className={classes.top_wrapper}>
          <Uploader
            isContentEditable={isContentEditable}
            initialData={initialData}
            setInitialData={setInitialData}
            setValue={setValue}
            setError={setError}
            getValues={getValues}
            clearErrors={clearErrors}
            error={errors?.icon?.message}
            setoLogo={setoLogo}
            logo={logo}
            trigger={trigger}
            label={formatMessage({
              id: 'developer_personal_page_info_logo',
            })}
          />
          <div className={classes.top_fields}>
          <label htmlFor="url" className={classes.informational_url} >
           {formatMessage({ id: 'description_descriptionURL' })}
           <span  className={classes.url_required}>*</span> 
          </label>
           <p className={classes.URL_helper_text} >
           {formatMessage({ id: 'description_descriptionURL_helping_text' })}
           </p>
            <TextField
              id="url"
              name="url"
              fieldId="desc_url"
              isRequired
              error={errors?.url?.message}
              control={control}
              disabled={!isContentEditable}
              autoComplete="off"
              maxLength={2000}
            />
            <Textarea
              id="long_description"
              name="long_description"
              fieldId="long_description"
              error={errors?.long_description?.message}
              isRequired
              control={control}
              disabled={!isContentEditable}
              label={formatMessage({ id: 'description_descriptionLabel' })}
              maxLength={5000}
            />
          </div>
        </div>
        <UploaderImg
          isContentEditable={isContentEditable}
          images={images}
          setImages={setImages}
          setValue={setValue}
          getValues={getValues}
          fieldId="preview_images"
          onRemoveImgUrl={onRemoveImgUrl}
          imagesUrl={imagesUrl}
          error={errorImg}
          errors={errors}
          setError={setErrorImg}
          clearErrors={clearErrors}
          trigger={trigger}
        />
        {isContentEditable && (
          <div className={classes.buttons_wrap}>
            <Button onClick={handleCancelEdit} variant="secondary">
              {formatMessage({ id: 'developer_app_desc_form_cancel_button' })}
            </Button>
            <Button type="submit" disabled={(isValid && !isDirty) || !isValid}>
              {formatMessage({ id: 'developer_app_desc_form_save_button' })}
            </Button>
          </div>
        )}
      </form>
      {loading && (
        <div className={classes.p_progress_spinner}>
          <ProgressSpinner />
        </div>
      )}
    </div>
  );
};
